import React from 'react';
import { SxProps, TextField as MuiTextField } from '@mui/material';
import { DefaultTheme, useTheme } from 'styled-components';
import { TextFieldProps } from './textfield.type';
import { getWebTextFieldSx } from './web-textfield-style';

export const getTextFieldSx = (
    theme: DefaultTheme,
    props: Pick<TextFieldProps, 'size' | 'radius' | 'width' | 'appearance' | 'fieldScale'>,
): SxProps => {
    const inputPadding = props.size === 'small' ? '1rem 1rem' : '1.4rem 2rem 0';
    const inputHeight = props.size === 'small' ? '3.6rem' : '4.2rem';

    return {
        margin: '0 !important',
        width: props.width,
        borderRadius: '99rem',
        color: theme.palette.common.text,
        fontWeight: theme.base.fontWeight,
        fontFamily: theme.base.fontFace,
        fontSize: theme.text.size.medium.fontSize,
        '& .MuiInputAdornment-root': {
            width: '4rem',
            height: '4rem',
            minWidth: '4rem',
            minHeight: '4rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '99rem',
            margin: 0,
            '& i': {
                width: '1.4rem',
                height: '1.4rem',
                color: theme.select.base.open.color,
            },
        },
        '& .MuiInputBase-root': {
            paddingRight: '0.7rem',
            paddingLeft: '0.7rem',
            borderRadius: '99rem',
            background: theme.palette.background.body,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.common.border,
            borderRadius: '99rem',
            top: 0,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.color.gray.main} !important`,
            borderWidth: '0.1rem',
        },
        '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline': {
            borderWidth: '0.1rem',
            borderColor: `${theme.palette.color.gray.main} !important`,
        },
        '& .MuiInputBase-root:focus-within .MuiOutlinedInput-notchedOutline': {
            borderWidth: '0.1rem',
            borderColor: `${theme.palette.color.gray.main} !important`,
        },
        '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.common.border} !important`,
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme.palette.color.negative.main} !important`,
            borderWidth: '0.1rem',
        },
        '& input': {
            padding: inputPadding,
            height: inputHeight,
            color: `${theme.palette.common.text} !important`,
            fontFamily: theme.base.fontFace,
            fontWeight: theme.base.fontWeight,
            fontSize: theme.text.size.medium.fontSize,
        },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.body} inset !important`,
            WebkitTextFillColor: `${theme.palette.common.text} !important`,
            transition: 'background-color 9999s ease-in-out 0s',
            caretColor: `${theme.palette.common.text} !important`,
        },
        '& input.Mui-disabled': {
            WebkitTextFillColor: `${theme.palette.common.textMuted} !important`,
            color: `${theme.palette.common.textMuted} !important`,
        },
        '& label': {
            top: '1.8rem',
            left: '2.5rem',
            color: theme.palette.common.textMuted,
            fontFamily: theme.base.fontFace,
            fontWeight: theme.base.fontWeight,
            fontSize: theme.text.size.medium.fontSize,
            lineHeight: theme.text.size.medium.lineHeight,
            letterSpacing: 0,
            transform: 'unset',
            transition: `all ${theme.base.transition}`,
        },
        '& label.Mui-focused, & label.MuiFormLabel-filled': {
            top: '1rem',
            color: theme.palette.common.textMuted,
            fontSize: theme.text.size.xsmall.fontSize,
            lineHeight: theme.text.size.xsmall.lineHeight,
        },
        '& legend': {
            display: 'none',
        },
        '& .MuiFormHelperText-root': {
            color: theme.palette.color.negative.main,
            fontFamily: theme.base.fontFace,
            fontWeight: 700,
            fontSize: theme.text.size.vsmall.fontSize,
        },
        '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
        '& input[type=number]': {
            MozAppearance: 'textfield',
        },
        ...(props.appearance === 'web' ? getWebTextFieldSx(props.fieldScale) : {}),
    };
};

export const TextField = (props: TextFieldProps) => {
    const { appearance, fieldScale, ...rest } = props;
    const theme = useTheme();

    return (
        <MuiTextField
            {...rest}
            data-web-textfield={appearance === 'web' || undefined}
            size={props.size as 'small' | 'medium' | undefined}
            className="w-full"
            sx={getTextFieldSx(theme, {
                size: props.size,
                radius: props.radius,
                width: props.width,
                appearance,
                fieldScale,
            })}
        />
    );
};
